import { useMemo } from 'react';
import type { IColumn } from '@creditflowresearch/uilib/Table/DataTable';
import type { TSetDraft } from '@/creditflowresearch/application/hooks/useDraftState';
import type { IColumnCustomizationEntry } from '@/creditflowresearch/application/BondScreener/Sidebar/useColumnsCustomization';
import { dragColumn } from './columns/dragColumn';
import useVisibleColumn from './columns/useVisibleColumn';
import { labelColumn } from './columns/labelColumn';
import { scopeColumn } from './columns/scopeColumn';
import useHideDeselectedColumn from './columns/useHideDeselectedColumn';
import useColumnOrdering from './columns/useColumnOrdering';

interface IUseColumnsTableProps {
  rows: IColumnCustomizationEntry[],
  getDraftColumns: () => IColumnCustomizationEntry[],
  setDraftColumns: TSetDraft<IColumnCustomizationEntry[]>,
  getHideDeselected: () => boolean,
  onToggleHideDeselected: () => void,
  showScopeColumn?: boolean,
}

interface IUseColumnsTableReturn {
  columns: IColumn[],
  handleOrderChange: (oldIndex: number, newIndex: number, displayedColumns: IColumnCustomizationEntry[]) => void,
}

const useColumnsTable = ({
  rows,
  getDraftColumns,
  setDraftColumns,
  getHideDeselected,
  onToggleHideDeselected,
  showScopeColumn = false,
}: IUseColumnsTableProps): IUseColumnsTableReturn => {
  const visibleColumn = useVisibleColumn({ rows, getDraftColumns, setDraftColumns });
  const hideDeselectedColumn = useHideDeselectedColumn({ getHideDeselected, onToggleHideDeselected });
  const { handleOrderChange } = useColumnOrdering(setDraftColumns);

  const columns: IColumn[] = useMemo(
    () => showScopeColumn
      ? [dragColumn, visibleColumn, labelColumn, scopeColumn, hideDeselectedColumn]
      : [dragColumn, visibleColumn, labelColumn, hideDeselectedColumn],
    [visibleColumn, hideDeselectedColumn, showScopeColumn],
  );

  return { columns, handleOrderChange };
};

export default useColumnsTable;
